"use client"

import { useRef, useState } from "react"
import { ExternalLink, Loader2, Send, Sparkles } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { ChannelIcon } from "@/components/ui/channel-icon"
import { Textarea } from "@/components/ui/textarea"
import {
  CHANNEL_TEXT_LIMITS,
  type ChannelTextLimit,
} from "@/lib/agents/messaging/channels/channel-text-limits"
import { measure } from "@/lib/agents/messaging/core/segment-text"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


/** The connected account a reply goes out as. Absent where the surface cannot name one. */
export interface ComposerIdentity {
  accountName: string | null
  avatarUrl?: string | null
  /** The account's own page, where it has a public one. */
  url?: string | null
}

interface ConversationComposerProps {
  /**
   * The channel a reply travels on. Its entry in `CHANNEL_TEXT_LIMITS` decides
   * whether a counter is shown and what it counts.
   */
  channelKind: string
  identity?: ComposerIdentity | null
  /**
   * Rejects to report a failure; the draft survives one, because retyping it is
   * not a fix. Resolving `{ keepDraft: true }` keeps it through a SUCCESS — the
   * outcome where nobody knows whether the mail left, and clearing the box would
   * destroy the only copy of what the operator wrote.
   */
  onSend: (text: string) => Promise<{ keepDraft?: boolean } | unknown>
  placeholder?: string
  /**
   * An AI reply that was drafted and never sent. **Offered, not loaded**: the box
   * stays empty until the operator presses «Use draft», so nothing is ever sent
   * under their name that they did not first read.
   */
  pendingDraft?: { text: string } | null
}

/**
 * Where a reply is written, in both inboxes.
 *
 * Rendered only where the surface can actually send — absent, never disabled. A
 * box that refuses every keystroke invites a message nobody can deliver.
 */
export function ConversationComposer({
  channelKind,
  identity,
  onSend,
  placeholder = "Type a message...",
  pendingDraft,
}: ConversationComposerProps) {
  const tUi = useTranslations("Feedback.ConversationComposer")

  const [draft, setDraft] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  // Null until the operator drags: CSS then decides the default per screen.
  const [textareaHeight, setTextareaHeight] = useState<number | null>(null)
  const [draftUsed, setDraftUsed] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // The handle captures the pointer, so the moves keep coming once a finger has
  // left it, and it ends on `pointercancel` as well as on release: a drag the
  // browser turned into a scroll used to leave the listeners behind, and the next
  // scroll of the transcript resized the box.
  const onDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const handle = event.currentTarget
    handle.setPointerCapture?.(event.pointerId)
    const startY = event.clientY
    // The rendered height, whichever of CSS or a previous drag set it.
    const startH = textareaRef.current?.offsetHeight || 80

    const onMove = (moved: PointerEvent) => {
      setTextareaHeight(Math.max(44, Math.min(320, startH + (startY - moved.clientY))))
    }
    const end = () => {
      handle.removeEventListener("pointermove", onMove)
      handle.removeEventListener("pointerup", end)
      handle.removeEventListener("pointercancel", end)
    }
    handle.addEventListener("pointermove", onMove)
    handle.addEventListener("pointerup", end)
    handle.addEventListener("pointercancel", end)
  }

  const send = async () => {
    const text = draft.trim()
    if (!text || isSending) return

    setIsSending(true)
    setSendError(null)
    try {
      const result = await onSend(text)
      if (!(result as { keepDraft?: boolean } | undefined)?.keepDraft) setDraft("")
    } catch (error) {
      // The draft stays: an operator who wrote a paragraph should not have to
      // write it again because the channel was briefly unreachable.
      setSendError(error instanceof Error ? error.message : "Failed to send")
    } finally {
      setIsSending(false)
    }
  }

  // `channelKind` is a plain string, not a messaging `ChannelType`: this component
  // is shared, and a shared component must not depend on one domain's union. A
  // channel with no entry simply has no limit to count against.
  const limit: ChannelTextLimit | undefined = (
    CHANNEL_TEXT_LIMITS as Partial<Record<string, ChannelTextLimit>>
  )[channelKind]
  const size = limit?.enforceOnSend ? measure(draft, limit.unit) : 0
  const overLimit = !!limit?.enforceOnSend && size > limit.max

  return (
    <div className="flex-shrink-0 border-t bg-background px-3 py-2 split:px-4 split:py-3">
      <div className="mx-auto max-w-2xl">
        {pendingDraft && !draftUsed && (
          <div className="mb-2 flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs dark:border-violet-900 dark:bg-violet-950/30">
            <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-violet-600 dark:text-violet-400" />
            <span className="flex-1 text-violet-700 dark:text-violet-300"><span key={tUi("aIDraftReady")}>{tUi("aIDraftReady")}</span></span>
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-xs"
              onClick={() => {
                setDraft(pendingDraft.text)
                setDraftUsed(true)
              }}
            >
              <span key={tUi("useDraft")}>{tUi("useDraft")}</span></Button>
          </div>
        )}
        {sendError && <p className="mb-2 text-xs text-red-500">{sendError}</p>}
        <div className="flex items-end gap-3">
          {identity?.accountName && (
            <div className="hidden flex-shrink-0 items-center self-stretch split:flex">
              <Avatar className="h-12 w-12">
                {identity.avatarUrl ? <AvatarImage src={identity.avatarUrl} /> : null}
                <AvatarFallback className="text-sm">
                  {identity.accountName.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
          <div className="min-w-0 flex-1">
            {identity?.accountName && (
              <p className="mb-1 hidden items-center gap-1 text-xs text-muted-foreground split:inline-flex">
                <ChannelIcon channelType={channelKind} size={14} className="shrink-0" />
                <span><span key={tUi("replyingAs")}>{tUi("replyingAs")}</span></span>
                {identity.url ? (
                  <a
                    href={identity.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={tUi("openValue0", { value0: String(identity.accountName) })}
                    className="inline-flex items-center gap-0.5 font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {identity.accountName}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="font-medium">{identity.accountName}</span>
                )}
              </p>
            )}
            {/* Drag handle — resize the textarea upward */}
            <div
              onPointerDown={onDragStart}
              className="group flex cursor-ns-resize touch-none select-none justify-center py-1"
            >
              <div className="h-1 w-8 rounded-full bg-muted-foreground/30 transition-colors group-hover:bg-muted-foreground/60" />
            </div>
            {/* Send sits in the textarea's own row so it stays flush with it — the
                character counter below must not push it out of line. */}
            <div className="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault()
                    void send()
                  }
                }}
                placeholder={placeholder}
                // 16px below `split`: iOS Safari zooms the page into any focused
                // field with a smaller font, which pushes Send past the right edge
                // and leaves the operator scrolled sideways mid-reply.
                className="h-11 min-h-0 flex-1 resize-none text-base split:h-20 split:text-sm"
                style={textareaHeight === null ? undefined : { height: `${textareaHeight}px` }}
                disabled={isSending}
              />
              <Button
                type="button"
                aria-label={tUi("sendMessage")}
                onClick={() => void send()}
                disabled={!draft.trim() || isSending}
                className="h-auto self-stretch bg-violet-600 px-3 hover:bg-violet-700 split:px-4 dark:bg-violet-600 dark:hover:bg-violet-500"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            {limit?.enforceOnSend && (
              <p
                className={cn(
                  "mt-1 text-right text-xs",
                  overLimit ? "text-amber-600" : "text-muted-foreground",
                )}
              >
                {tUi(overLimit ? `${limit.unit}CounterSplit` : `${limit.unit}Counter`, {size, max: limit.max})}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
